import { Shield, Eye, Lock, UserCheck, Clock, Mail } from 'lucide-react'
import SEOHead from '../components/SEOHead'

export default function Privacy() {
  return (
    <div>
      <SEOHead 
        title="Privacy Policy - C9 Recruitment Data Protection & Cookie Policy"
        description="C9 Recruitment's comprehensive privacy policy. Learn how we protect your personal data, use cookies, and comply with GDPR regulations."
        keywords="privacy policy, data protection, GDPR, cookies, personal data, C9 recruitment privacy"
        canonical="/privacy"
      />
      {/* Hero Section */}
      <section className="gradient-bg text-white section-padding">
        <div className="container-full text-center">
          <Shield className="h-16 w-16 text-c9-orange mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            Privacy <span className="text-c9-orange">Policy</span>
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-200 mb-8">
            We take your privacy seriously. This policy explains how we collect, 
            use, and protect your personal information.
          </p>
          <div className="text-gray-200">
            Last updated: June 2025
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-gray-50 py-8">
        <div className="container-full">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="#collection" className="text-c9-blue hover:text-c9-orange">Data Collection</a>
            <a href="#usage" className="text-c9-blue hover:text-c9-orange">How We Use Data</a>
            <a href="#sharing" className="text-c9-blue hover:text-c9-orange">Data Sharing</a>
            <a href="#security" className="text-c9-blue hover:text-c9-orange">Security</a>
            <a href="#rights" className="text-c9-blue hover:text-c9-orange">Your Rights</a>
            <a href="#contact" className="text-c9-blue hover:text-c9-orange">Contact Us</a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-full max-w-4xl">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">Our Commitment to Your Privacy</h2>
            <div className="card p-8">
              <p className="text-gray-600 mb-4">
                C9 Recruitment Limited ("we", "us", "our") is committed to protecting and respecting your privacy. 
                This privacy policy explains when and why we collect personal information about people who visit our 
                website, use our services, or interact with us, how we use it, the conditions under which we may 
                disclose it to others, and how we keep it secure.
              </p>
              <p className="text-gray-600">
                We may change this policy from time to time by updating this page. You should check this page 
                occasionally to ensure that you are happy with any changes.
              </p>
            </div>
          </div>

          {/* Data Collection */}
          <div id="collection" className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">
              <Eye className="inline h-8 w-8 text-c9-orange mr-3" />
              What Information We Collect
            </h2>
            <div className="card p-8">
              <h3 className="text-xl font-semibold text-c9-blue mb-4">Personal Information</h3>
              <ul className="list-disc ml-6 space-y-2 text-gray-600 mb-6">
                <li>Name, address, email address, and phone number</li>
                <li>Employment history and CV details</li>
                <li>Educational qualifications and professional licenses</li>
                <li>References and character information</li>
                <li>Right to work documentation</li>
                <li>Bank details for payroll purposes (temporary staff)</li>
              </ul>

              <h3 className="text-xl font-semibold text-c9-blue mb-4">Technical Information</h3>
              <ul className="list-disc ml-6 space-y-2 text-gray-600">
                <li>IP address and browser information</li>
                <li>Pages visited and time spent on our website</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Device information and operating system</li>
              </ul>
            </div>
          </div>

          {/* Data Usage */}
          <div id="usage" className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">
              <UserCheck className="inline h-8 w-8 text-c9-orange mr-3" />
              How We Use Your Information
            </h2>
            <div className="card p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold text-c9-blue mb-4">For Candidates</h3>
                  <ul className="list-disc ml-6 space-y-2 text-gray-600">
                    <li>Matching you with suitable job opportunities</li>
                    <li>Processing job applications</li>
                    <li>Conducting background checks</li>
                    <li>Communicating about positions</li>
                    <li>Providing career advice and support</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-c9-blue mb-4">For Clients</h3>
                  <ul className="list-disc ml-6 space-y-2 text-gray-600">
                    <li>Understanding recruitment requirements</li>
                    <li>Providing candidate recommendations</li>
                    <li>Managing recruitment processes</li>
                    <li>Invoicing and account management</li>
                    <li>Market research and industry insights</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Data Sharing */}
          <div id="sharing" className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">When We Share Your Information</h2>
            <div className="card p-8">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-800 font-semibold">
                  We will never sell your personal information to third parties.
                </p>
              </div>
              
              <h3 className="text-xl font-semibold text-c9-blue mb-4">We may share your information with:</h3>
              <ul className="list-disc ml-6 space-y-2 text-gray-600">
                <li><strong>Potential employers</strong> - With your explicit consent for job applications</li>
                <li><strong>Reference providers</strong> - To verify employment history and qualifications</li>
                <li><strong>Compliance agencies</strong> - For DBS checks and right to work verification</li>
                <li><strong>Professional advisors</strong> - Legal, accounting, and business advisors</li>
                <li><strong>Service providers</strong> - IT support, payroll, and administrative services</li>
                <li><strong>Regulatory bodies</strong> - When required by law or regulation</li>
              </ul>
            </div>
          </div>

          {/* Security */}
          <div id="security" className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">
              <Lock className="inline h-8 w-8 text-c9-orange mr-3" />
              How We Protect Your Information
            </h2>
            <div className="card p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold text-c9-blue mb-4">Technical Measures</h3>
                  <ul className="list-disc ml-6 space-y-2 text-gray-600">
                    <li>SSL encryption for data transmission</li>
                    <li>Secure servers and databases</li>
                    <li>Regular security updates and patches</li>
                    <li>Access controls and authentication</li>
                    <li>Regular data backups</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-c9-blue mb-4">Organizational Measures</h3>
                  <ul className="list-disc ml-6 space-y-2 text-gray-600">
                    <li>Staff training on data protection</li>
                    <li>Confidentiality agreements</li>
                    <li>Regular security audits</li>
                    <li>Incident response procedures</li>
                    <li>Data retention policies</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div id="rights" className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">Your Rights</h2>
            <div className="card p-8">
              <p className="text-gray-600 mb-6">
                Under UK GDPR, you have the following rights regarding your personal data:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="h-2 w-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-c9-blue">Right to Access</strong>
                      <p className="text-gray-600 text-sm">Request copies of your personal data</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="h-2 w-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-c9-blue">Right to Rectification</strong>
                      <p className="text-gray-600 text-sm">Request corrections to inaccurate data</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="h-2 w-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-c9-blue">Right to Erasure</strong>
                      <p className="text-gray-600 text-sm">Request deletion of your personal data</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="h-2 w-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-c9-blue">Right to Portability</strong>
                      <p className="text-gray-600 text-sm">Receive your data in a structured format</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="h-2 w-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-c9-blue">Right to Object</strong>
                      <p className="text-gray-600 text-sm">Object to processing of your personal data</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="h-2 w-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-c9-blue">Right to Withdraw Consent</strong>
                      <p className="text-gray-600 text-sm">Withdraw consent for data processing</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Retention */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">
              <Clock className="inline h-8 w-8 text-c9-orange mr-3" />
              Data Retention
            </h2>
            <div className="card p-8">
              <p className="text-gray-600 mb-4">
                We retain your personal information for different periods depending on the purpose:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-gray-600">
                <li><strong>Candidate records:</strong> Up to 7 years after last contact</li>
                <li><strong>Client records:</strong> Up to 7 years after contract end</li>
                <li><strong>Website data:</strong> Up to 2 years from collection</li>
                <li><strong>Marketing data:</strong> Until you unsubscribe or withdraw consent</li>
                <li><strong>Financial records:</strong> 7 years as required by law</li>
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div id="contact" className="mb-12">
            <h2 className="text-3xl font-bold text-c9-blue mb-6">
              <Mail className="inline h-8 w-8 text-c9-orange mr-3" />
              Contact Us About Privacy
            </h2>
            <div className="card p-8">
              <p className="text-gray-600 mb-6">
                If you have any questions about this privacy policy or wish to exercise your rights, 
                please contact us:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-c9-blue mb-3">Data Protection Officer</h3>
                  <div className="space-y-2 text-gray-600">
                    <div>📧 contact@c9-recruitment.com</div>
                    <div>📞 0208 152 4574</div>
                    <div>📍 C9 Recruitment Limited<br />Albany House, 162-168 High Street<br />Waltham Cross, EN8 7DF</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-c9-blue mb-3">Response Times</h3>
                  <div className="space-y-2 text-gray-600">
                    <div>• Data access requests: Within 30 days</div>
                    <div>• General privacy queries: Within 5 days</div>
                    <div>• Urgent matters: Within 24 hours</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ICO Information */}
      <section className="bg-gray-50 section-padding">
        <div className="container-full text-center">
          <div className="card p-8 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-c9-blue mb-4">Complaints</h2>
            <p className="text-gray-600 mb-4">
              If you are not satisfied with our response to your privacy concerns, you have the right to 
              complain to the Information Commissioner's Office (ICO).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
              <div>
                <strong>ICO Website:</strong><br />
                www.ico.org.uk
              </div>
              <div>
                <strong>ICO Phone:</strong><br />
                0303 123 1113
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
